import React, { useState, useCallback, useEffect } from 'react';
import { GALLERY_BASE_URL } from '../../config';
import '../../styles/PhotoGallery.css';

const MagnifierIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
    className="photo-gallery-magnifier"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export default function PhotoGallery({ accountId, photos = [] }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const sortedPhotos = [...photos].sort((a, b) => a.sort_order - b.sort_order);
  const featuredPhoto = sortedPhotos[0];
  const masonryPhotos = sortedPhotos.slice(1);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const showPrev = useCallback(() => {
    setLightboxIndex((i) => (i > 0 ? i - 1 : sortedPhotos.length - 1));
  }, [sortedPhotos.length]);

  const showNext = useCallback(() => {
    setLightboxIndex((i) => (i < sortedPhotos.length - 1 ? i + 1 : 0));
  }, [sortedPhotos.length]);

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  if (!sortedPhotos.length) return null;

  const photoUrl = (photo) => `${GALLERY_BASE_URL}/${accountId}/${photo.id}`;

  return (
    <section className="photo-gallery" aria-label="معرض صور الصالون">
      <h2 className="photo-gallery-title">
        <span className="photo-gallery-title-text">معرض الصور</span>
      </h2>

      {featuredPhoto && (
        <button
          type="button"
          className="photo-gallery-featured"
          onClick={() => setLightboxIndex(0)}
          aria-label="عرض الصورة الرئيسية"
        >
          <img src={photoUrl(featuredPhoto)} alt="" loading="lazy" />
          <span className="photo-gallery-thumb-overlay" aria-hidden="true">
            <MagnifierIcon />
          </span>
        </button>
      )}

      {masonryPhotos.length > 0 && (
        <div className="photo-gallery-masonry">
          {masonryPhotos.map((photo, idx) => (
            <button
              key={photo.id}
              type="button"
              className="photo-gallery-thumb"
              onClick={() => setLightboxIndex(idx + 1)}
              aria-label={`عرض صورة ${idx + 2}`}
            >
              <img
                src={photoUrl(photo)}
                alt=""
                loading="lazy"
                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
              />
              <span className="photo-gallery-thumb-overlay" aria-hidden="true">
                <MagnifierIcon />
              </span>
            </button>
          ))}
        </div>
      )}

      {lightboxIndex !== null && (
        <div
          className="photo-gallery-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="عرض الصورة"
        >
          <button
            type="button"
            className="photo-gallery-lightbox-close"
            onClick={closeLightbox}
            aria-label="إغلاق"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <button
            type="button"
            className="photo-gallery-lightbox-nav photo-gallery-lightbox-prev"
            onClick={(e) => { e.stopPropagation(); showPrev(); }}
            aria-label="الصورة السابقة"
          >
            ‹
          </button>
          <img
            className="photo-gallery-lightbox-img"
            src={photoUrl(sortedPhotos[lightboxIndex])}
            alt=""
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="photo-gallery-lightbox-nav photo-gallery-lightbox-next"
            onClick={(e) => { e.stopPropagation(); showNext(); }}
            aria-label="الصورة التالية"
          >
            ›
          </button>
          <span className="photo-gallery-lightbox-counter">
            {lightboxIndex + 1} / {sortedPhotos.length}
          </span>
        </div>
      )}
    </section>
  );
}
